/**
 * useEquipmentManager — 全功能资源节点管理 Hook
 *
 * 职责：数据加载、筛选、CRUD、CIP、影响分析、导出备份
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { processTemplateV2Api } from '../../services';
import type {
  ResourceNode,
  ResourceNodeClass,
  ResourceNodePayload,
  ResourceNodeSchedulingProfilePayload,
  PlannerOperation,
} from '../ProcessTemplateV2/types';
import {
  flattenNodes,
  buildNodeMap,
  buildNodePath,
  findNode,
  BINDABLE_CLASSES,
  allowedChildBlueprints,
  buildNodeCodePreview,
  type NodeBlueprint,
} from './resourceNodeConstants';
import {
  filterResourceNodeTree,
  resourceNodeHasIssue,
  type SystemTypeFilter,
} from './resourceNodeFilters';

export type { SystemTypeFilter } from './resourceNodeFilters';

/* ────────────────── Types ────────────────── */

export type ViewMode = 'card' | 'table' | 'tree';

export interface RoomGroup {
  roomNode: ResourceNode;
  equipmentNodes: ResourceNode[];
}

export interface NodeStats {
  totalCount: number;
  roomCount: number;
  bindableCount: number;
  mappedResourceCount: number;
  unboundCount: number;
  inactiveCount: number;
  issueCount: number;
  susByType: number;
  ssByType: number;
  virtualByType: number;
}

/* ────────────────── Hook ────────────────── */

export function useEquipmentManager(templateId?: number) {
  /* ── State ── */
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [nodes, setNodes] = useState<ResourceNode[]>([]);
  const [impactOperations, setImpactOperations] = useState<PlannerOperation[]>([]);

  const [search, setSearch] = useState('');
  const [systemTypeFilter, setSystemTypeFilter] = useState<SystemTypeFilter>('ALL');
  const [nodeClassFilter, setNodeClassFilter] = useState<ResourceNodeClass | 'ALL'>('ALL');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  /* ── Load data ── */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const promises: Promise<any>[] = [
        processTemplateV2Api.listResourceNodes({ includeInactive: true, tree: true }),
      ];

      if (templateId) {
        promises.push(processTemplateV2Api.getPlanner(templateId));
      }

      const results = await Promise.all(promises);
      const [nodeTree] = results;

      setNodes(nodeTree);

      if (templateId && results[1]) {
        setImpactOperations(results[1].operations ?? []);
      }

      if (nodeTree.length > 0) {
        setSelectedNodeId((current) => current ?? nodeTree[0].id);
      } else {
        setSelectedNodeId(null);
      }
    } catch (err: any) {
      console.error('Failed to load resource node data:', err);
      setNodes([]);
      setImpactOperations([]);
      setErrorMessage('资源节点管理加载失败，请先确认资源节点表和资源中心模型已就绪。');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  /* ── Derived data ── */
  const allNodes = useMemo(() => flattenNodes(nodes), [nodes]);
  const nodeMap = useMemo(() => buildNodeMap(allNodes), [allNodes]);
  const selectedNode = useMemo(() => findNode(nodes, selectedNodeId), [nodes, selectedNodeId]);

  const selectedNodePath = useMemo(() => {
    if (!selectedNode) return '';
    return buildNodePath(selectedNode.id, nodeMap)
      .map((n) => n.nodeName)
      .join(' / ');
  }, [nodeMap, selectedNode]);

  const childBlueprints = useMemo<NodeBlueprint[]>(
    () => allowedChildBlueprints(selectedNode),
    [selectedNode],
  );

  /* ── Filtering ── */
  const filteredTree = useMemo(
    () => filterResourceNodeTree(nodes, {
      search,
      nodeClass: nodeClassFilter,
      systemType: systemTypeFilter,
      issuesOnly,
    }),
    [issuesOnly, nodeClassFilter, nodes, search, systemTypeFilter],
  );

  const filteredNodes = useMemo(() => flattenNodes(filteredTree), [filteredTree]);

  /* ── Room groups (for card view) ── */
  const roomGroups = useMemo<RoomGroup[]>(() => {
    const rooms = allNodes.filter((n) => n.nodeClass === 'ROOM');
    return rooms.map((room) => ({
      roomNode: room,
      equipmentNodes: allNodes.filter((n) => n.parentId === room.id),
    }));
  }, [allNodes]);

  /* ── Statistics ── */
  const nodeStats = useMemo<NodeStats>(() => {
    const equipNodes = allNodes.filter((n) => n.nodeClass === 'EQUIPMENT_UNIT');
    const bindableNodes = allNodes.filter((n) => BINDABLE_CLASSES.has(n.nodeClass));
    const mappedResourceCount = bindableNodes.filter((n) => Boolean(n.boundResourceId)).length;
    return {
      totalCount: allNodes.length,
      roomCount: allNodes.filter((n) => n.nodeClass === 'ROOM').length,
      bindableCount: bindableNodes.length,
      mappedResourceCount,
      unboundCount: bindableNodes.length - mappedResourceCount,
      inactiveCount: allNodes.filter((n) => !n.isActive).length,
      issueCount: allNodes.filter(resourceNodeHasIssue).length,
      susByType: equipNodes.filter((n) => n.equipmentSystemType === 'SUS').length,
      ssByType: equipNodes.filter((n) => n.equipmentSystemType === 'SS').length,
      virtualByType: equipNodes.filter((n) => n.equipmentSystemType === 'VIRTUAL').length,
    };
  }, [allNodes]);

  /* ── Impact operations ── */
  const selectedNodeImpactOps = useMemo(
    () =>
      selectedNode
        ? impactOperations.filter(
            (op) => Number(op.defaultResourceNodeId) === Number(selectedNode.id),
          )
        : [],
    [impactOperations, selectedNode],
  );

  /* ── Room & parent options ── */
  const roomNodeOptions = useMemo(
    () => allNodes.filter((n) => n.nodeClass === 'ROOM'),
    [allNodes],
  );

  const parentOptionsForClass = useCallback(
    (nodeClass: ResourceNodeClass): ResourceNode[] => {
      switch (nodeClass) {
        case 'SITE':
          return []; // root
        case 'LINE':
          return allNodes.filter((n) => n.nodeClass === 'SITE');
        case 'ROOM':
          return allNodes.filter(
            (n) => n.nodeClass === 'SITE' || n.nodeClass === 'LINE' || n.nodeClass === 'ROOM',
          );
        case 'EQUIPMENT_UNIT':
          return allNodes.filter(
            (n) => n.nodeClass === 'ROOM' && n.nodeSubtype === 'MAIN_PROCESS',
          );
        case 'COMPONENT':
          return allNodes.filter((n) => n.nodeClass === 'EQUIPMENT_UNIT');
        case 'UTILITY_STATION':
          return allNodes.filter(
            (n) => n.nodeClass === 'ROOM' && n.nodeSubtype === 'UTILITY_SHARED',
          );
        default:
          return allNodes;
      }
    },
    [allNodes],
  );

  /* ── Code preview ── */
  const getCodePreview = useCallback(
    (nodeScope: string, departmentCode: string | null, nodeClass: ResourceNodeClass) =>
      buildNodeCodePreview(nodeScope as any, departmentCode, nodeClass, allNodes),
    [allNodes],
  );

  /* ── CRUD ── */
  const createNode = useCallback(
    async (payload: ResourceNodePayload) => {
      const createdId = await processTemplateV2Api.createResourceNode(payload);
      setSelectedNodeId(createdId);
      await loadData();
      return createdId;
    },
    [loadData],
  );

  const updateNode = useCallback(
    async (nodeId: number, payload: Partial<ResourceNodePayload>) => {
      await processTemplateV2Api.updateResourceNode(nodeId, payload);
      await loadData();
    },
    [loadData],
  );

  const deleteNode = useCallback(
    async (nodeId: number) => {
      await processTemplateV2Api.deleteResourceNode(nodeId);
      if (selectedNodeId === nodeId) setSelectedNodeId(null);
      await loadData();
    },
    [loadData, selectedNodeId],
  );

  const toggleActive = useCallback(
    async (nodeId: number, isActive: boolean) => {
      await processTemplateV2Api.updateResourceNode(nodeId, { isActive });
      await loadData();
    },
    [loadData],
  );

  const moveNode = useCallback(
    async (nodeId: number, parentId: number | null, sortOrder?: number) => {
      await processTemplateV2Api.moveResourceNode(nodeId, { parentId, sortOrder });
      await loadData();
    },
    [loadData],
  );

  /* ── Batch ── */
  const batchToggleActive = useCallback(
    async (ids: number[], isActive: boolean) => {
      await Promise.all(
        ids.map((id) => processTemplateV2Api.updateResourceNode(id, { isActive })),
      );
      setSelectedIds([]);
      await loadData();
    },
    [loadData],
  );

  const batchDelete = useCallback(
    async (ids: number[]) => {
      await Promise.all(ids.map((id) => processTemplateV2Api.deleteResourceNode(id)));
      setSelectedIds([]);
      await loadData();
    },
    [loadData],
  );

  /* ── Scheduling profile ── */
  const updateSchedulingProfile = useCallback(
    async (nodeId: number, payload: ResourceNodeSchedulingProfilePayload) => {
      await processTemplateV2Api.updateResourceNodeSchedulingProfile(nodeId, payload);
      await loadData();
    },
    [loadData],
  );

  /* ── Export & rebuild ── */
  const exportBackup = useCallback(() => {
    const payload = {
      exportedAt: new Date().toISOString(),
      nodes,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `resource-nodes-backup-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, [nodes]);

  const clearForRebuild = useCallback(async () => {
    await processTemplateV2Api.clearResourceNodeTreeForRebuild();
    setSelectedNodeId(null);
    await loadData();
  }, [loadData]);

  /* ── Return ── */
  return {
    // State
    loading,
    errorMessage,
    nodes,
    allNodes,
    nodeMap,

    // Selection
    selectedNodeId,
    setSelectedNodeId,
    selectedNode,
    selectedNodePath,
    selectedIds,
    setSelectedIds,
    childBlueprints,

    // Filters
    search,
    setSearch,
    systemTypeFilter,
    setSystemTypeFilter,
    nodeClassFilter,
    setNodeClassFilter,
    issuesOnly,
    setIssuesOnly,
    viewMode,
    setViewMode,

    // Derived
    filteredNodes,
    filteredTree,
    roomGroups,
    nodeStats,
    selectedNodeImpactOps,
    roomNodeOptions,
    parentOptionsForClass,
    getCodePreview,

    // CRUD
    createNode,
    updateNode,
    deleteNode,
    toggleActive,
    moveNode,

    // Batch
    batchToggleActive,
    batchDelete,

    // Scheduling profile
    updateSchedulingProfile,

    // Export
    exportBackup,
    clearForRebuild,

    // Refresh
    refresh: loadData,
  };
}
